import React, { useEffect, useRef, useState } from 'react';
import { CloseButton, Modal, Textarea } from '@mantine/core';
import {
  ASSISTANT_HISTORY_MAX_TURNS,
  ASSISTANT_HISTORY_TURN_MAX_LENGTH,
  ASSISTANT_QUESTION_MAX_LENGTH,
  ASSISTANT_QUESTION_MIN_LENGTH,
  AssistantHistoryTurn,
} from 'shared-models/browser';
import { Button } from '../../../../components/Button';
import { assistantService } from '../../../../services/assistant/assistant.service';
import { toHebrewApiErrorMessage } from '../../../../services/index/index.service';
import { AssistantFeedbackModal } from './AssistantFeedbackModal';
import {
  BanIcon,
  ChevronLeftIcon,
  CloseIcon,
  CoinIcon,
  FlameIcon,
  InfoIcon,
  MessageIcon,
  PencilIcon,
  SendIcon,
  SparklesIcon,
  TrendingUpIcon,
  UsersIcon,
} from './assistant-icons';
import classes from './assistant-panel.module.scss';

type Message = { role: 'user' | 'assistant'; content: string };

// After a failed turn the capped tail can open on an assistant turn, which the API rejects.
export const historyForRequest = (messages: Message[]): AssistantHistoryTurn[] => {
  const tail = messages.slice(-ASSISTANT_HISTORY_MAX_TURNS);
  const firstUser = tail.findIndex((message) => message.role === 'user');
  return (firstUser < 0 ? [] : tail.slice(firstUser))
    .map((message) => ({ role: message.role, text: message.content.slice(0, ASSISTANT_HISTORY_TURN_MAX_LENGTH) }));
};

const REQUEST_TIMEOUT_MS = 45_000;
const TIMEOUT_REASON = 'assistant-timeout';
const TIMEOUT_MESSAGE = 'הבדיקה ארכה יותר מדי זמן. נסו שאלה ממוקדת יותר.';
const SUBTITLE = 'שאלו כל שאלה על הנתונים של המסעדה';
const STATUS_LABEL = 'מחובר לדאטה';
const QUICK_QUESTIONS_LABEL = 'שאלות מהירות';
const DISCLAIMER = 'העוזר מבוסס על AI ויכול לטעות';
const FEEDBACK_LABEL = 'הערה לשיפור?';
const EMPTY_QUESTION_ERROR = 'כתבו שאלה קודם';
const LOADING_LABEL = 'בודק את הנתונים, זה יכול לקחת עד דקה...';

const SUGGESTIONS: { text: string; Icon: React.FC<{ className?: string }> }[] = [
  { text: 'איזה עובדים נתנו בראש השבוע?', Icon: UsersIcon },
  { text: 'מה המנות הכי חזקות השבוע?', Icon: FlameIcon },
  { text: 'כמה נמכר היום לעומת אתמול?', Icon: TrendingUpIcon },
  { text: 'כמה ביטולים היו הערב ומי ביצע אותם?', Icon: BanIcon },
  { text: 'כמה טיפ גבינו הערב?', Icon: CoinIcon },
];

export const AssistantPanel: React.FC<{ tenantId: string; opened: boolean; onClose: () => void }> = ({ tenantId, opened, onClose }) => {
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputError, setInputError] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const inFlightRef = useRef(false);

  useEffect(() => () => abortRef.current?.abort(), []);

  // Drops the running question; its own cleanup then sees a foreign controller and stands down.
  const abortInFlight = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    inFlightRef.current = false;
    setLoading(false);
  };

  // Any close is a full reset: the conversation is dropped and an in-flight question is aborted.
  const reset = () => {
    abortInFlight();
    setMessages([]);
    setQuestion('');
    setError(null);
    setInputError(false);
    onClose();
  };

  // Editing turn N aborts a running answer, restores the text and drops that turn and later ones.
  const editMessage = (index: number) => {
    abortInFlight();
    setQuestion(messages[index]?.content ?? '');
    setMessages((current) => current.slice(0, index));
    setError(null);
    setInputError(false);
  };

  const ask = async (nextQuestion = question) => {
    const trimmed = nextQuestion.trim();
    if (trimmed.length < ASSISTANT_QUESTION_MIN_LENGTH) {
      setInputError(true);
      return;
    }
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setQuestion('');
    setError(null);
    setInputError(false);
    const history = historyForRequest(messages);
    setMessages((current) => [...current, { role: 'user', content: trimmed }]);
    setLoading(true);
    const controller = new AbortController();
    abortRef.current = controller;
    const timeoutId = window.setTimeout(() => controller.abort(TIMEOUT_REASON), REQUEST_TIMEOUT_MS);
    try {
      const answer = await assistantService.ask(tenantId, trimmed, history, controller.signal);
      if (abortRef.current === controller) {
        setMessages((current) => [...current, { role: 'assistant', content: answer }]);
      }
    } catch (caught: unknown) {
      if (controller.signal.reason === TIMEOUT_REASON) {
        setError(TIMEOUT_MESSAGE);
      } else if (!controller.signal.aborted) {
        setError(caught instanceof Error ? toHebrewApiErrorMessage(caught.message) : 'אירעה שגיאה. נסו שוב.');
      }
    } finally {
      window.clearTimeout(timeoutId);
      // A reset or an edit already took over: its fresh state must not be undone by this cleanup.
      if (abortRef.current === controller) {
        abortRef.current = null;
        inFlightRef.current = false;
        setLoading(false);
      }
    }
  };

  const isEmpty = messages.length === 0 && !loading;

  const handleQuestionChange = (value: string) => {
    setQuestion(value);
    setInputError(false);
  };

  // Enter sends, Shift+Enter keeps the multi-line newline the textarea allows.
  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    void ask();
  };

  return (
    <>
    <Modal
      opened={opened}
      onClose={reset}
      withCloseButton={false}
      padding={0}
      size="lg"
      centered
      dir="rtl"
      classNames={{ content: classes.modalContent, body: classes.modalBody }}
    >
      <div className={classes.panel} data-testid="assistant-panel">
        <header className={classes.header}>
          <div className={classes.headerRow}>
            <span className={classes.iconTile} aria-hidden="true"><SparklesIcon className={classes.iconTileGlyph} /></span>
            <div className={classes.headerText}>
              <div className={classes.headerTitleRow}>
                <h2 className={classes.title}>עוזר עסקי</h2>
                <span className={classes.statusPill}>
                  <span className={classes.statusDot} aria-hidden="true" />
                  <span className={classes.statusLabel}>{STATUS_LABEL}</span>
                </span>
              </div>
              <p className={classes.subtitle}>{SUBTITLE}</p>
            </div>
            <CloseButton
              className={classes.close}
              icon={<CloseIcon className={classes.closeIcon} />}
              onClick={reset}
              aria-label="סגירה"
              data-testid="assistant-panel-close"
            />
          </div>
        </header>
        <div className={classes.body}>
          {isEmpty ? (
            <div className={classes.questions} data-testid="assistant-panel-empty">
              <p className={classes.quickLabel}>{QUICK_QUESTIONS_LABEL}</p>
              <div className={classes.suggestions}>
                {SUGGESTIONS.map((suggestion, index) => (
                  <Button
                    key={suggestion.text}
                    variant="cancel"
                    size="sm"
                    className={classes.suggestion}
                    dataTestId={`assistant-panel-suggestion-${index}`}
                    onClick={() => void ask(suggestion.text)}
                  >
                    <span className={classes.suggestionTile} aria-hidden="true">
                      <suggestion.Icon className={classes.suggestionIcon} />
                    </span>
                    <span className={classes.suggestionText}>{suggestion.text}</span>
                    <ChevronLeftIcon className={classes.suggestionChevron} />
                  </Button>
                ))}
              </div>
            </div>
          ) : null}
          <div className={classes.messages} aria-live="polite" data-testid="assistant-panel-messages">
            {messages.map((message, index) => (message.role === 'assistant' ? (
              <div
                key={`${message.role}-${index}`}
                className={classes.assistantMessage}
                data-testid={`assistant-panel-message-${index}`}
              >
                {message.content}
              </div>
            ) : (
              <div key={`${message.role}-${index}`} className={classes.userRow}>
                <Button
                  variant="icon"
                  className={classes.editButton}
                  ariaLabel="עריכת השאלה"
                  dataTestId={`assistant-panel-message-edit-${index}`}
                  onClick={() => editMessage(index)}
                >
                  <PencilIcon className={classes.editIcon} />
                </Button>
                <div className={classes.userMessage} data-testid={`assistant-panel-message-${index}`}>
                  {message.content}
                </div>
              </div>
            )))}
            {loading ? <div className={classes.assistantMessage} data-testid="assistant-panel-loading">{LOADING_LABEL}</div> : null}
          </div>
          {error ? <div className={classes.error} data-testid="assistant-panel-error">{error}</div> : null}
          <div className={classes.inputArea}>
            <div className={classes.composer} data-testid="assistant-panel-composer">
              <Textarea
                value={question}
                onChange={(event) => handleQuestionChange(event.currentTarget.value)}
                placeholder="שאל שאלה על העסק..."
                maxLength={ASSISTANT_QUESTION_MAX_LENGTH}
                rows={1}
                onKeyDown={handleInputKeyDown}
                className={classes.input}
                classNames={{ wrapper: classes.inputWrapper }}
                data-testid="assistant-panel-input"
              />
              <Button
                variant="confirm"
                loading={loading}
                loadingLabel=""
                className={classes.sendButton}
                ariaLabel="שלח"
                dataTestId="assistant-panel-send"
                onClick={() => void ask()}
              >
                <SendIcon className={classes.sendIcon} />
              </Button>
            </div>
            {inputError ? (
              <p className={classes.inputError} data-testid="assistant-panel-input-error">{EMPTY_QUESTION_ERROR}</p>
            ) : null}
            <div className={classes.footer}>
              <span className={classes.disclaimer} data-testid="assistant-panel-disclaimer">
                <InfoIcon className={classes.disclaimerIcon} />
                {DISCLAIMER}
              </span>
              <Button
                variant="cancel"
                size="sm"
                className={classes.feedbackButton}
                dataTestId="assistant-panel-feedback-open"
                onClick={() => setIsFeedbackOpen(true)}
              >
                <MessageIcon className={classes.feedbackIcon} />
                <span>{FEEDBACK_LABEL}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
    <AssistantFeedbackModal tenantId={tenantId} opened={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
    </>
  );
};
